/* ═══════════════════════════════════════════════════════════════════════════
   Comms — guest communications: today-actions feed, comm panel, payment
   quick-pick, and the shared send-and-mark flow (incl. the Stripe link
   helper). Extracted from App.jsx (2026-05-29); see decomposition spec.
   ═══════════════════════════════════════════════════════════════════════════ */
import { useState, useEffect, useRef } from "react";
import { supabase } from './supabase';
import { cleanPhone, computeRequestedDeposit } from './utils';
import { interpolateTemplate, getTemplateText, buildTemplateVars } from './templates.js';
import { relevantCircles, CIRCLE_KEYS, buildTodayFeed, countTodayActions } from './comm-tracker.js';
import { CL, FONT, st, payCol } from './theme.js';
import { CHANNEL_STYLES, channelButtonStyle } from './channels.js';
import { SectionHeader, ConfirmModal, DepositAmountModal } from './ui.jsx';

/* ═══ STRIPE PAYMENT LINK HELPER ═══ */
async function generateStripePaymentLink({ propertyId, propertyName, currency, amount, checkIn, checkOut }) {
  const d1 = new Date(checkIn + 'T00:00:00');
  const d2 = new Date(checkOut + 'T00:00:00');
  const mo = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const dateStr = (d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear())
    ? `${d1.getDate()}–${d2.getDate()} ${mo[d1.getMonth()]} ${d1.getFullYear()}`
    : `${d1.getDate()} ${mo[d1.getMonth()]} – ${d2.getDate()} ${mo[d2.getMonth()]} ${d2.getFullYear()}`;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(
      `${import.meta.env.VITE_SUPA_URL}/functions/v1/create-payment-link`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({
          property_id: propertyId,
          amount: Number(amount),
          currency: (currency || 'EUR').toLowerCase(),
          description: `Stay at ${propertyName} · ${dateStr}`,
        }),
      }
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || 'Stripe error' };
    if (!data.url) return { error: 'Stripe error' };
    return { url: data.url };
  } catch {
    return { error: 'network' };
  }
}

const _sendAndMarkInFlight = new Set();

/* ═══ SHARED SEND-AND-MARK HELPER ═══ */
async function sendAndMark({ booking, key, channel, room, property, lang, onUpdated, showToastWithUndo, requestedAmount }) {
  let paymentLink = '';

  if (key === 'deposit_request') {
    const amountNum = Number(requestedAmount);
    if (!amountNum || amountNum <= 0) {
      // The caller is responsible for prompting via DepositAmountModal before calling
      // sendAndMark. If we somehow reach here without a valid amount, bail silently.
      showToastWithUndo?.(lang === 'bg' ? 'Липсва сума на депозита' : 'Missing deposit amount');
      return;
    }
    if (property?.stripe_restricted_key) {
      const genKey = `${booking.id}:${key}`;
      if (_sendAndMarkInFlight.has(genKey)) return;
      _sendAndMarkInFlight.add(genKey);
      let result;
      try {
        result = await generateStripePaymentLink({
          propertyId: property.id,
          propertyName: property.name,
          currency: property.currency,
          amount: amountNum,
          checkIn: booking.checkIn,
          checkOut: booking.checkOut,
        });
      } finally {
        _sendAndMarkInFlight.delete(genKey);
      }
      if (result.error) {
        showToastWithUndo?.(lang === 'bg' ? 'Грешка с Stripe' : 'Stripe error');
        return;
      }
      paymentLink = result.url;
    }
  }

  const overrideBooking = key === 'deposit_request'
    ? { ...booking, depositAmount: Number(requestedAmount) }
    : booking;
  const vars = buildTemplateVars(overrideBooking, room || null, property, lang, { paymentLink });
  let text = getTemplateText(property, key, lang);
  if (key === 'confirmation' && booking.source === 'Booking.com') {
    text = text + getTemplateText(property, 'confirmation_bcom_suffix', lang);
  }
  const msg = interpolateTemplate(text, vars);

  // Open the deep link FIRST while we're still in the user-gesture context;
  // any await before window.open makes the browser treat the open as
  // programmatic and the popup blocker may swallow it. Then optimistically
  // mark the row sent locally so it disappears from the feed immediately, and
  // fire-and-forget the DB write — if it fails we revert the local state.
  const phone = booking.phone?.replace(/[^0-9]/g, '') || '';
  if (channel === 'whatsapp') window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
  else if (channel === 'viber') {
    navigator.clipboard?.writeText(msg);
    window.location.href = `viber://chat?number=${phone}`;
  } else {
    const subjects = {
      confirmation: `Booking confirmation · ${property?.name}`,
      deposit_request: `Deposit request · ${property?.name}`,
      balance_reminder: `Balance reminder · ${property?.name}`,
      arrival_reminder: `See you tomorrow · ${property?.name}`,
      review_request: `Thank you for staying with us`,
    };
    window.location.href = `mailto:${booking.email}?subject=${encodeURIComponent(subjects[key] || '')}&body=${encodeURIComponent(msg)}`;
  }

  const now = new Date().toISOString();
  const nextLog = { ...(booking.messageLog || {}), [key + '_sent_at']: now };
  delete nextLog[key + '_skipped_at'];
  const updated = { ...booking, messageLog: nextLog };
  onUpdated?.(updated);

  supabase.from('bookings').update({ message_log: nextLog }).eq('id', booking.id)
    .then(({ error }) => {
      if (error) {
        onUpdated?.(booking);  // revert
        showToastWithUndo?.(lang === 'bg' ? 'Грешка при запис' : 'Save failed');
        return;
      }
      showToastWithUndo?.(
        lang === 'bg' ? 'Маркирано като изпратено · Отмени' : 'Marked as sent — Undo',
        { undo: async () => {
            const revertedLog = { ...nextLog };
            delete revertedLog[key + '_sent_at'];
            const { error: undoErr } = await supabase.from('bookings').update({ message_log: revertedLog }).eq('id', booking.id);
            if (undoErr) {
              // UI now disagrees with DB — re-apply the optimistic state and
              // tell the owner the undo didn't stick.
              onUpdated?.({ ...booking, messageLog: nextLog });
              showToastWithUndo?.(lang === 'bg' ? 'Отмяната не успя' : 'Undo failed');
              return;
            }
            onUpdated?.({ ...booking, messageLog: revertedLog });
          } }
      );
    });
}

/* ═══ TODAY ACTIONS FEED ═══ */
function TodayActionsFeed({ bookings, rooms, property, lang, guestContactPrefs, guestTrusted, onOpenBooking, onUpdateBooking, showToastWithUndo, hideHeader = false }) {
  const today = new Date().toISOString().slice(0, 10);
  const feed = buildTodayFeed(bookings, property, today, guestTrusted);
  const totalCount = countTodayActions(bookings, property, today, guestTrusted);
  const [pendingManualPrompt, setPendingManualPrompt] = useState(null);
  if (feed.length === 0 && !pendingManualPrompt) return null;

  const FULL_LABELS = {
    confirmation: lang === 'bg' ? 'Потвърждение' : 'Confirmation',
    deposit_request: lang === 'bg' ? 'Искане на депозит' : 'Deposit request',
    balance_reminder: lang === 'bg' ? 'Напомняне за баланс' : 'Balance reminder',
    arrival_reminder: lang === 'bg' ? 'Напомняне за пристигане' : 'Arrival reminder',
    review_request: lang === 'bg' ? 'Искане за отзив' : 'Review request',
  };
  const STATUS_DOT = { late: '#C0562B', due: '#C9A96E' };

  function preferredChannelFor(booking) {
    const phoneKey = booking.phone ? booking.phone.replace(/[\s\-\(\)\.\+]/g, '') : null;
    const rawPref = (phoneKey && guestContactPrefs[phoneKey]) || booking.preferredContact || 'whatsapp';
    const pref = rawPref === 'phone' ? 'whatsapp' : rawPref;
    const phone = booking.phone?.replace(/[^0-9]/g, '') || '';
    if (pref === 'email' && !booking.email) return phone ? 'whatsapp' : null;
    if (pref !== 'email' && !phone) return booking.email ? 'email' : null;
    return pref;
  }

  function handleSend(row, channel) {
    const room = (rooms || []).find(r => r.id === row.booking.roomId) || null;

    if (row.key === 'deposit_request') {
      const requested = computeRequestedDeposit(row.booking, property);
      if (requested === null || !requested || requested <= 0) {
        // manual mode, OR auto mode with insufficient data → prompt
        setPendingManualPrompt({ booking: row.booking, channel, room });
        return;
      }
      sendAndMark({
        booking: row.booking, key: row.key, channel, room,
        property, lang, onUpdated: onUpdateBooking, showToastWithUndo,
        requestedAmount: requested,
      });
      return;
    }

    sendAndMark({
      booking: row.booking, key: row.key, channel, room,
      property, lang, onUpdated: onUpdateBooking, showToastWithUndo,
    });
  }

  return (
    <div style={{ marginBottom: 14 }}>
      {!hideHeader && (
        <SectionHeader>
          {lang === 'bg' ? `Действия днес · ${totalCount}` : `Actions today · ${totalCount}`}
        </SectionHeader>
      )}
      <div style={{ margin: '0 14px' }}>
      {feed.map(row => {
        const channel = preferredChannelFor(row.booking);
        const ChannelIcon = channel ? CHANNEL_STYLES[channel].Icon : null;
        return (
          <div key={row.bookingId + row.key}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 4px', borderBottom: `1px solid ${CL.sand}` }}>
            <div style={{ width: 10, height: 10, borderRadius: 5, background: STATUS_DOT[row.status], flexShrink: 0 }} />
            <div onClick={() => onOpenBooking(row.booking)} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#3D2B1F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {FULL_LABELS[row.key]} · {row.booking.guestName}
              </div>
            </div>
            {channel ? (
              <button
                onClick={(e) => { e.stopPropagation(); handleSend(row, channel); }}
                style={{ ...channelButtonStyle(channel), flex: 'none', padding: '6px 10px' }}>
                <ChannelIcon sz={14} />
                <span>{CHANNEL_STYLES[channel].label}</span>
              </button>
            ) : (
              <div onClick={() => onOpenBooking(row.booking)} style={{ fontSize: 11, color: '#8B7D6B', flexShrink: 0, cursor: 'pointer' }}>
                {lang === 'bg' ? 'Отвори →' : 'Open →'}
              </div>
            )}
          </div>
        );
      })}
      </div>

      {pendingManualPrompt && (
        <DepositAmountModal
          guestName={pendingManualPrompt.booking.guestName}
          currency={property?.currency}
          channel={pendingManualPrompt.channel}
          lang={lang}
          onCancel={() => setPendingManualPrompt(null)}
          onSubmit={(amount) => {
            const { booking, channel, room } = pendingManualPrompt;
            setPendingManualPrompt(null);
            sendAndMark({
              booking, key: 'deposit_request', channel, room,
              property, lang, onUpdated: onUpdateBooking, showToastWithUndo,
              requestedAmount: amount,
            });
          }}
        />
      )}
    </div>
  );
}

function TrustedToggle({ booking, guestTrusted, saveTrusted, lang }) {
  const phoneKey = cleanPhone(booking.phone);
  const enabled = !!(phoneKey && guestTrusted?.[phoneKey]);
  const noPhone = !phoneKey;

  function toggle() {
    if (noPhone) return;
    saveTrusted(phoneKey, !enabled);
  }

  return (
    <div
      style={{
        margin: '0 14px 10px',
        padding: '10px 12px',
        background: enabled ? '#FBF6EC' : '#FFFEF9',
        border: '1px solid ' + (enabled ? '#C9A96E' : '#E8D5B0'),
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        opacity: noPhone ? 0.5 : 1,
      }}
      title={noPhone ? (lang === 'bg' ? 'Добавете телефон, за да активирате' : 'Add a phone number to enable') : ''}
    >
      <span style={{ fontSize: 18 }}>⭐</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#3D2B1F' }}>
          {lang === 'bg' ? 'Доверен гост' : 'Trusted guest'}
        </div>
        <div style={{ fontSize: 11, color: '#8B7D6B', marginTop: 2 }}>
          {lang === 'bg' ? 'Пропускайте искането за депозит и напомнянето за баланс' : 'Skip deposit + balance reminders'}
        </div>
      </div>
      <button
        onClick={toggle}
        disabled={noPhone}
        aria-label={enabled ? 'Mark as not trusted' : 'Mark as trusted'}
        style={{
          width: 48, height: 28, borderRadius: 14,
          background: enabled ? '#5A7247' : '#E8D5B0',
          border: 'none',
          cursor: noPhone ? 'not-allowed' : 'pointer',
          position: 'relative',
        }}
      >
        <span style={{
          position: 'absolute',
          top: 3,
          left: enabled ? 23 : 3,
          width: 22, height: 22, borderRadius: 11,
          background: '#fff',
          transition: 'left 0.15s',
        }} />
      </button>
    </div>
  );
}

function PaymentQuickPick({ booking, totalPrice, cur, lang, t, property, onUpdateBooking, showToast, inSurface = false }) {
  const [status, setStatus] = useState(booking.paymentStatus);
  const [depositInput, setDepositInput] = useState(() => {
    if (booking.depositAmount) return String(booking.depositAmount);
    const requested = computeRequestedDeposit(booking, property);
    return requested && requested > 0 ? String(requested) : '';
  });
  const [saving, setSaving] = useState(false);
  const [editingDeposit, setEditingDeposit] = useState(!booking.depositAmount);
  const [confirmPaid, setConfirmPaid] = useState(false);
  const bookingRef = useRef(booking);
  bookingRef.current = booking;  // keeps ref current on every render

  useEffect(() => {
    setStatus(booking.paymentStatus);
    if (booking.depositAmount) {
      setDepositInput(String(booking.depositAmount));
    } else {
      const requested = computeRequestedDeposit(booking, property);
      setDepositInput(requested && requested > 0 ? String(requested) : '');
    }
    setEditingDeposit(!booking.depositAmount);
  }, [booking.id, booking.paymentStatus, booking.depositAmount, property?.deposit_policy_mode, property?.deposit_policy_value]);

  async function saveStatus(newStatus) {
    const prev = status;
    setStatus(newStatus);
    setSaving(true);
    const { error } = await supabase.from('bookings').update({ payment_status: newStatus }).eq('id', bookingRef.current.id);
    setSaving(false);
    if (error) {
      setStatus(prev);
      showToast(lang === 'bg' ? 'Грешка при запис' : 'Error saving payment status');
      return;
    }
    const updated = { ...bookingRef.current, paymentStatus: newStatus };
    bookingRef.current = updated;
    onUpdateBooking(updated);
  }

  async function saveDepositAmount() {
    if (saving) return;
    const num = Number(depositInput) || 0;
    setEditingDeposit(false);
    if (num === Number(bookingRef.current.depositAmount || 0)) return;
    const { error } = await supabase.from('bookings').update({ deposit_amount: num }).eq('id', bookingRef.current.id);
    if (error) {
      showToast(lang === 'bg' ? 'Грешка при запис' : 'Error saving deposit amount');
      setEditingDeposit(true);
      return;
    }
    const updated = { ...bookingRef.current, depositAmount: num };
    bookingRef.current = updated;
    onUpdateBooking(updated);
  }

  const STATUSES = ['Unpaid', 'Deposit', 'Paid'];

  const balance = Math.max(0, (Number(totalPrice) || 0) - (Number(depositInput) || 0));
  const depositLbl = lang === 'bg' ? 'Депозит' : 'Deposit';
  const unpaidLbl  = lang === 'bg' ? 'Неплатено' : 'Unpaid';
  const paidLbl    = lang === 'bg' ? 'Платено' : 'Paid';
  const leftLbl    = lang === 'bg' ? 'остават' : 'left';
  const setAmtLbl  = lang === 'bg' ? 'Задай сума' : 'Set amount';
  const unpaidHelperLbl = lang === 'bg'
    ? 'Натиснете Депозит или Платено след получаване на средствата.'
    : "Press Deposit or Paid once you've received the funds.";
  const depositPromptLbl    = lang === 'bg' ? 'Въведете получения депозит.' : "Enter the deposit you've received.";
  const depositHelperLbl    = lang === 'bg' ? 'Маркирайте Депозит само след получаване на средствата. След това въведете сумата и натиснете Запис.' : 'Only mark Deposit once funds have arrived. Then enter the amount and Save.';
  const confirmPaidTitleLbl = lang === 'bg' ? 'Маркиране като платено?' : 'Mark as fully paid?';
  const confirmPaidBodyLbl  = Number(totalPrice) > 0
    ? (lang === 'bg' ? `Потвърдете, че пълната сума ${cur}${totalPrice} е получена от госта.` : `Confirm the full ${cur}${totalPrice} has been received from the guest.`)
    : (lang === 'bg' ? 'Потвърдете, че пълната сума е получена от госта.' : 'Confirm the full amount has been received from the guest.');
  const confirmPaidCancelLbl = lang === 'bg' ? 'Отказ' : 'Cancel';
  const confirmPaidGoLbl     = lang === 'bg' ? 'Маркирай като платено' : 'Mark as paid';

  // Pill labels — the DEPOSIT pill shows the amount only when Deposit is the active status.
  // Once a booking is fully Paid (or back to Unpaid) we don't want a stale "DEPOSIT €50"
  // hanging on the inactive pill.
  const labelFor = (s) => {
    if (s === 'Unpaid') return unpaidLbl;
    if (s === 'Paid')   return paidLbl;
    if (status === 'Deposit' && Number(depositInput) > 0) return `${depositLbl} ${cur}${depositInput}`;
    return depositLbl;
  };

  // When rendered inside the booking-detail cream surface (`inSurface`), drop the outer
  // card chrome — the parent .sec already provides background, padding, hairline, and the
  // diamond + uppercase header. Otherwise render as the standalone card we use elsewhere.
  const wrapperStyle = inSurface
    ? null
    : { margin: '0 14px 10px', padding: '10px 12px', background: CL.cream, border: '1px solid ' + CL.goldL, borderRadius: 10 };
  return (
    <div style={wrapperStyle || undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {!inSurface && (
          <div style={{ fontSize: 11, fontWeight: 700, color: CL.stone, letterSpacing: 1, textTransform: 'uppercase' }}>
            {lang === 'bg' ? 'Плащане' : 'Payment'}
          </div>
        )}
        {/* Pill group — active fill = status colour (terra/gold-brown/olive) so the booking-detail
            agrees with the list badge; inactive stays transparent with stone text. Paid is gated
            behind a ConfirmModal so accidental clicks don't mark a booking as fully collected. */}
        <div style={{ display: 'flex', borderRadius: 999, overflow: 'hidden', border: `1px solid ${CL.goldL}`, background: CL.cream, marginLeft: inSurface ? 'auto' : 0 }}>
          {STATUSES.map((s, i) => {
            const active = s === status;
            const isLast = i === STATUSES.length - 1;
            return (
              <button
                key={s}
                onClick={() => {
                  if (saving || active) return;
                  if (s === 'Paid') setConfirmPaid(true);
                  else saveStatus(s);
                }}
                style={{
                  padding: '6px 11px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  border: 'none',
                  borderRight: isLast ? 'none' : `1px solid ${CL.goldL}`,
                  fontFamily: FONT.body,
                  cursor: saving || active ? 'default' : 'pointer',
                  background: active ? payCol(s) : 'transparent',
                  color: active ? '#fff' : CL.stone,
                  transition: 'background 0.12s',
                }}
              >
                {labelFor(s)}
              </button>
            );
          })}
        </div>
        {/* Inline balance + pencil-edit affordance — clicking opens the deposit editor so the
            owner can adjust the deposit/balance split. */}
        {status === 'Deposit' && Number(depositInput) > 0 && !editingDeposit && (
          <button onClick={() => setEditingDeposit(true)} style={{
            background: 'none', border: `1px solid ${CL.goldL}`, borderRadius: 999,
            padding: '3px 9px', cursor: 'pointer', fontSize: 12, color: CL.stone,
            fontFamily: FONT.body, display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <span>{cur}{balance} {leftLbl}</span>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
            </svg>
          </button>
        )}
        {status === 'Deposit' && Number(depositInput) <= 0 && !editingDeposit && (
          <button onClick={() => setEditingDeposit(true)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: CL.olive, fontFamily: FONT.body, fontWeight: 600 }}>
            {setAmtLbl} →
          </button>
        )}
      </div>
      {status === 'Unpaid' && (
        <div style={{ marginTop: 8, fontSize: 12, color: CL.stone, lineHeight: 1.45 }}>
          {unpaidHelperLbl}
        </div>
      )}
      {/* Deposit amount editor — when actively editing, lead with a clear prompt + helper so
          the receptionist knows what to enter and when it's appropriate to mark Deposit. */}
      {status === 'Deposit' && editingDeposit && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: CL.bark, marginBottom: 4 }}>
            {depositPromptLbl}
          </div>
          <div style={{ fontSize: 12, color: CL.stone, lineHeight: 1.45, marginBottom: 10 }}>
            {depositHelperLbl}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: CL.stone, pointerEvents: 'none' }}>{cur}</span>
              <input
                id={`deposit-amt-${booking.id}`}
                autoFocus type="number" inputMode="decimal" placeholder="0"
                value={depositInput}
                onChange={e => setDepositInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveDepositAmount()}
                style={{ ...st.inp, fontSize: 14, padding: '8px 10px 8px 24px' }}
              />
            </div>
            <button onClick={saveDepositAmount} style={{ background: CL.olive, color: '#fff', border: 'none', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
              {lang === 'bg' ? 'Запис' : 'Save'}
            </button>
          </div>
        </div>
      )}
      {confirmPaid && (
        <ConfirmModal
          title={confirmPaidTitleLbl}
          body={confirmPaidBodyLbl}
          cancelLabel={confirmPaidCancelLbl}
          confirmLabel={confirmPaidGoLbl}
          onCancel={() => setConfirmPaid(false)}
          onConfirm={async () => { setConfirmPaid(false); await saveStatus('Paid'); }}
        />
      )}
    </div>
  );
}

function formatSentAt(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '';
  const day = d.getDate();
  const mon = d.toLocaleString('en-GB', { month: 'short' });
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${mon} at ${hh}:${mm}`;
}

/* ═══ COMMUNICATIONS PANEL ═══ */
function CommPanel({ booking, room, property, lang, guestContactPrefs, guestTrusted, onUpdateBooking, showToast, inSurface = false }) {
  const [commLang, setCommLang] = useState(lang);
  const [openKey, setOpenKey] = useState(null);
  const [tipShow, setTipShow] = useState(false);
  const [depositInput, setDepositInput] = useState(() => {
    if (booking.depositAmount) return String(booking.depositAmount);
    const requested = computeRequestedDeposit(booking, property);
    return requested && requested > 0 ? String(requested) : '';
  });
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [showMsgForSent, setShowMsgForSent] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [channelMenuOpen, setChannelMenuOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);

  // Re-sync deposit input when the booking record changes (e.g. after save or re-open)
  useEffect(() => {
    if (booking.depositAmount) {
      setDepositInput(String(booking.depositAmount));
      return;
    }
    const requested = computeRequestedDeposit(booking, property);
    setDepositInput(requested && requested > 0 ? String(requested) : '');
  }, [booking.id, booking.depositAmount, booking.pricePerNight, property?.deposit_policy_mode, property?.deposit_policy_value]);

  // Clear stale error/loading state when popover closes or switches to a different circle
  useEffect(() => {
    setGenError('');
    setGenerating(false);
    setShowMsgForSent(false);
  }, [openKey]);

  const today = new Date().toISOString().slice(0, 10);

  // Surface a one-time nudge to record the guest's preferred channel — but only on the
  // 2nd+ open of a booking, so we don't distract the receptionist while they're still
  // entering data on first open. Tracked per-booking in localStorage.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const seenKey = `commTipSeen:${booking.id}`;
    const dismissKey = `commTipDismissed:${booking.id}`;
    if (window.localStorage.getItem(dismissKey) === '1') return;
    if (window.localStorage.getItem(seenKey)) {
      setTipShow(true);
    } else {
      window.localStorage.setItem(seenKey, '1');
    }
  }, [booking.id]);

  function dismissTip() {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(`commTipDismissed:${booking.id}`, '1');
    }
    setTipShow(false);
  }

  const circles = relevantCircles(booking, property, today, guestTrusted).filter(c => c.status !== 'hidden');
  if (circles.length === 0) return null;

  // Pick the most-urgent un-sent circle for the "Next action" card.
  // Priority: late > due > soon > locked > skipped > sent.
  // Tiebreak by CIRCLE_KEYS order (canonical timeline).
  const NEXT_ACTION_RANK = { late: 0, due: 1, soon: 2, locked: 3, skipped: 4, sent: 5 };
  const nextAction = [...circles].sort((a, b) => {
    const r = (NEXT_ACTION_RANK[a.status] ?? 99) - (NEXT_ACTION_RANK[b.status] ?? 99);
    if (r !== 0) return r;
    return CIRCLE_KEYS.indexOf(a.key) - CIRCLE_KEYS.indexOf(b.key);
  })[0];
  // "All done" = every relevant circle is either sent or skipped. Skipping counts as
  // handled — the owner deliberately decided not to send that one.
  const allDone = circles.every(c => c.status === 'sent' || c.status === 'skipped');
  const reviewSent = circles.find(c => c.key === 'review_request')?.status === 'sent';

  const showPrefTip = tipShow && !booking.preferredContact && !!(booking.phone || booking.email);

  const phone = booking.phone?.replace(/[^0-9]/g, '') || '';
  const phoneKey = booking.phone ? booking.phone.replace(/[\s\-\(\)\.\+]/g, '') : null;
  const rawPref = (phoneKey && guestContactPrefs[phoneKey]) || booking.preferredContact || 'whatsapp';
  const preferredChannel = rawPref === 'phone' ? 'whatsapp' : rawPref;
  const activeChannel = selectedChannel || preferredChannel;
  const vars = buildTemplateVars(booking, room, property, commLang);

  function getMsg(key) {
    let text = getTemplateText(property, key, commLang);
    if (key === 'confirmation' && booking.source === 'Booking.com') {
      text = text + getTemplateText(property, 'confirmation_bcom_suffix', commLang);
    }
    // For deposit_request, override depositAmount with the live input value so the
    // preview reflects what the user is about to send.
    let liveVars = vars;
    if (key === 'deposit_request') {
      const liveAmount = Number(depositInput) || 0;
      liveVars = { ...vars, depositAmount: liveAmount || '' };
    }
    return interpolateTemplate(text, liveVars);
  }

  async function patchLog(patch) {
    const nextLog = { ...(booking.messageLog || {}), ...patch };
    // Strip any keys whose value is null (used to delete entries).
    for (const k of Object.keys(patch)) if (patch[k] === null) delete nextLog[k];
    const { error } = await supabase.from('bookings').update({ message_log: nextLog }).eq('id', booking.id);
    if (error) { showToast?.(lang === 'bg' ? 'Грешка при запис' : 'Save failed'); return; }
    onUpdateBooking?.({ ...booking, messageLog: nextLog });
  }

  function markSent(key) {
    const now = new Date().toISOString();
    patchLog({ [key + '_sent_at']: now, [key + '_skipped_at']: null });
    showToast?.(lang === 'bg' ? 'Маркирано като изпратено · Отмени' : 'Marked as sent — Undo',
      { undo: () => patchLog({ [key + '_sent_at']: null }) });
  }

  function markSkipped(key) {
    const now = new Date().toISOString();
    patchLog({ [key + '_skipped_at']: now, [key + '_sent_at']: null });
  }

  function unskip(key) {
    patchLog({ [key + '_skipped_at']: null });
  }

  async function sendVia(channel, key) {
    setGenError('');

    // For deposit_request, optionally generate a Stripe link and persist deposit amount
    let paymentLink = '';
    if (key === 'deposit_request') {
      const amountNum = Number(depositInput);
      if (!amountNum || amountNum <= 0) {
        setGenError(lang === 'bg' ? 'Въведете сума' : 'Enter an amount');
        return;
      }

      if (property?.stripe_restricted_key) {
        setGenerating(true);
        let result;
        try {
          result = await generateStripePaymentLink({
            propertyId: property.id,
            propertyName: property.name,
            currency: property.currency,
            amount: amountNum,
            checkIn: booking.checkIn,
            checkOut: booking.checkOut,
          });
        } finally {
          setGenerating(false);
        }
        if (result.error) {
          setGenError(
            result.error === 'network'
              ? (lang === 'bg' ? 'Мрежова грешка. Опитайте отново.' : 'Network error. Please try again.')
              : result.error
          );
          return;
        }
        paymentLink = result.url;
      }
    }

    // Build vars with live deposit amount + (optional) Stripe link
    let liveVars = vars;
    if (key === 'deposit_request') {
      const amountNum = Number(depositInput) || 0;
      liveVars = buildTemplateVars(
        { ...booking, depositAmount: amountNum },
        room,
        property,
        commLang,
        { paymentLink }
      );
    }

    let text = getTemplateText(property, key, commLang);
    if (key === 'confirmation' && booking.source === 'Booking.com') {
      text = text + getTemplateText(property, 'confirmation_bcom_suffix', commLang);
    }
    const msg = interpolateTemplate(text, liveVars);

    // Mark sent BEFORE opening the deep link — see sendAndMark for rationale.
    markSent(key);
    if (channel === 'whatsapp') window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
    else if (channel === 'viber') {
      navigator.clipboard?.writeText(msg);
      window.location.href = `viber://chat?number=${phone}`;
    } else {
      const subjects = {
        confirmation: `Booking confirmation · ${property?.name}`,
        deposit_request: `Deposit request · ${property?.name}`,
        balance_reminder: `Balance reminder · ${property?.name}`,
        arrival_reminder: `See you tomorrow · ${property?.name}`,
        review_request: `Thank you for staying with us`,
      };
      window.location.href = `mailto:${booking.email}?subject=${encodeURIComponent(subjects[key] || '')}&body=${encodeURIComponent(msg)}`;
    }
  }

  const STATUS_FILL = {
    sent: '#5A7247', late: '#C0562B', due: '#C9A96E',
    soon: '#E8D5B0', skipped: '#8B7D6B', locked: '#8B7D6B',
  };
  const SHORT_LABELS = {
    confirmation: lang === 'bg' ? 'Потв.' : 'Conf',
    deposit_request: lang === 'bg' ? 'Деп.'  : 'Dep',
    balance_reminder: lang === 'bg' ? 'Бал.' : 'Bal',
    arrival_reminder: lang === 'bg' ? 'Прист.' : 'Arr',
    review_request: lang === 'bg' ? 'Отз.' : 'Rev',
  };
  const FULL_LABELS = {
    confirmation: lang === 'bg' ? 'Потвърждение' : 'Confirmation',
    deposit_request: lang === 'bg' ? 'Искане на депозит' : 'Deposit request',
    balance_reminder: lang === 'bg' ? 'Напомняне за баланс' : 'Balance reminder',
    arrival_reminder: lang === 'bg' ? 'Напомняне за пристигане' : 'Arrival reminder',
    review_request: lang === 'bg' ? 'Искане за отзив' : 'Review request',
  };

  const NEXT_ACTION_TITLES = {
    confirmation: lang === 'bg' ? 'Изпрати потвърждение' : 'Send booking confirmation',
    deposit_request: lang === 'bg' ? 'Поискай депозит' : 'Request deposit',
    balance_reminder: lang === 'bg' ? 'Напомни за остатъка' : 'Send balance reminder',
    arrival_reminder: lang === 'bg' ? 'Изпратете инструкции за пристигане' : 'Send check-in info',
    review_request: lang === 'bg' ? 'Поискай отзив' : 'Ask for a review',
  };

  const NEXT_ACTION_TIMING = {
    confirmation: lang === 'bg' ? 'След създаване на резервация' : 'When booking is made',
    deposit_request: lang === 'bg' ? 'След създаване на резервация' : 'When booking is made',
    balance_reminder: lang === 'bg' ? '3 дни преди пристигане' : '3 days before arrival',
    arrival_reminder: lang === 'bg' ? '1 ден преди пристигане' : '1 day before arrival',
    review_request: lang === 'bg' ? 'Ден след отпътуване' : 'Day after check-out',
  };

  const LOCK_HINT = {
    confirmation: lang === 'bg' ? 'Добавете телефон или имейл' : 'Add phone or email',
    deposit_request: (() => {
      if (booking.paymentStatus !== 'Unpaid')
        return lang === 'bg' ? 'Депозитът вече е отбелязан като получен в резервацията' : 'Deposit already collected — recorded in booking form';
      if (booking.source === 'Booking.com')
        return lang === 'bg' ? 'Booking.com резервации: плащането се управлява директно' : 'Booking.com bookings — payment handled directly';
      return lang === 'bg' ? 'Попълнете банкови данни или Stripe ключ в Настройки' : 'Add bank details or Stripe key in Settings';
    })(),
    balance_reminder: lang === 'bg' ? 'Маркирайте като Депозит' : 'Mark payment status as Deposit',
    arrival_reminder: lang === 'bg' ? 'Добавете телефон или имейл' : 'Add phone or email',
    review_request: lang === 'bg' ? 'Добавете Google Review линк в Настройки' : 'Add Google Review link in Settings',
  };

  const channelOrder = ['whatsapp', 'viber', 'email'].sort((a, b) =>
    a === preferredChannel ? -1 : b === preferredChannel ? 1 : 0
  );

  // When rendered inside the booking-detail cream surface, the parent .sec provides the
  // diamond + uppercase "MESSAGES" header and the hairline divider. CommPanel just renders
  // a small EN/BG toggle (right-aligned) and its content. Standalone, it keeps its own
  // bordered card and "MESSAGES" caption for use elsewhere in the app.
  const outerStyle = inSurface
    ? { overflow: 'hidden' }
    : { margin: '0 14px 14px', background: '#fff', border: '1px solid ' + CL.goldL, borderRadius: 12, overflow: 'hidden' };
  return (
    <div style={outerStyle}>
      {!inSurface && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid ' + CL.goldL }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: CL.bark, textTransform: 'uppercase', letterSpacing: 1 }}>
            {lang === 'bg' ? 'Съобщения' : 'Messages'}
          </div>
          <div style={{ display: 'flex', background: CL.sand, borderRadius: 6, overflow: 'hidden' }}>
            {['en', 'bg'].map(l => (
              <button key={l} onClick={() => setCommLang(l)}
                style={{ padding: '4px 10px', border: 'none', background: commLang === l ? CL.bark : 'transparent', color: commLang === l ? CL.cream : CL.stone, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Next action card — default view inside the booking-detail surface */}
      {inSurface && (() => {
        if (allDone) {
          // Only show a "Show all N" toggle when there's more than one circle —
          // for Booking.com bookings (single review_request circle) the toggle
          // adds nothing.
          const showToggle = circles.length > 1;
          const subtitle = reviewSent
            ? (lang === 'bg'
                ? 'Поискали сте отзив. Това беше последното съобщение.'
                : "You've sent the review request — that's the final message.")
            : (lang === 'bg'
                ? `Всички ${circles.length} действия са изпълнени.`
                : `All ${circles.length} actions handled.`);
          return (
            <div>
              <div style={{ fontFamily: FONT.display, fontSize: 20, color: CL.bark, marginBottom: 4, lineHeight: 1.15 }}>
                {lang === 'bg' ? 'Готово' : 'All done'}
              </div>
              <div style={{ fontSize: 11.5, color: CL.stone, marginBottom: 12 }}>
                {subtitle}
              </div>
              {showToggle && (
                <button onClick={() => setShowAll(v => !v)}
                  style={{ display: 'block', margin: '12px auto 0', background: 'none', border: 'none', color: CL.gold, fontSize: 10.5, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
                  {showAll
                    ? (lang === 'bg' ? 'Скрий ▴' : 'Hide ▴')
                    : (lang === 'bg' ? `Покажете всички ${circles.length} ›` : `Show all ${circles.length} ›`)}
                </button>
              )}
            </div>
          );
        }

        if (!nextAction) return null;
        const { key: naKey, status: naStatus } = nextAction;
        const naLocked = naStatus === 'locked';
        const naSkipped = naStatus === 'skipped';

        const phoneOk = !!phone;
        const emailOk = !!booking.email;
        const availableChannels = [
          phoneOk && 'whatsapp',
          phoneOk && 'viber',
          emailOk && 'email',
        ].filter(Boolean);
        const chipChannel = availableChannels.includes(activeChannel) ? activeChannel : availableChannels[0];

        const ChannelIcon = chipChannel ? CHANNEL_STYLES[chipChannel].Icon : null;
        const channelLabel = chipChannel ? CHANNEL_STYLES[chipChannel].label : '';

        return (
          <div>
            <div style={{ fontFamily: FONT.display, fontSize: 20, color: CL.bark, marginBottom: 4, lineHeight: 1.15 }}>
              {NEXT_ACTION_TITLES[naKey]}
            </div>
            <div style={{ fontSize: 11.5, color: CL.stone, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span>{NEXT_ACTION_TIMING[naKey]}</span>
              {chipChannel && (
                <>
                  <span style={{ color: CL.goldL }}>·</span>
                  <span style={{ position: 'relative' }}>
                    <button
                      onClick={() => setChannelMenuOpen(v => !v)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '2px 8px', borderRadius: 5,
                        background: CL.cream, border: '1px solid ' + CL.goldL,
                        fontSize: 10.5, fontWeight: 600, color: CL.bark,
                        cursor: availableChannels.length > 1 ? 'pointer' : 'default',
                        fontFamily: 'inherit',
                      }}>
                      <ChannelIcon sz={11} />
                      <span>{channelLabel}</span>
                      {availableChannels.length > 1 && <span style={{ fontSize: 9 }}>▾</span>}
                    </button>
                    {channelMenuOpen && availableChannels.length > 1 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 20,
                        background: CL.cream, border: '1px solid ' + CL.goldL, borderRadius: 8,
                        padding: 4, minWidth: 110, boxShadow: '0 4px 12px rgba(61,43,31,0.12)',
                      }}>
                        {availableChannels.map(ch => {
                          const ChIcon = CHANNEL_STYLES[ch].Icon;
                          return (
                            <button key={ch}
                              onClick={() => { setSelectedChannel(ch); setChannelMenuOpen(false); }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                width: '100%', padding: '6px 8px',
                                background: ch === chipChannel ? CL.sand : 'transparent',
                                border: 'none', borderRadius: 4,
                                fontSize: 11, fontWeight: 600, color: CL.bark,
                                cursor: 'pointer', textAlign: 'left',
                              }}>
                              <ChIcon sz={11} />
                              <span>{CHANNEL_STYLES[ch].label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </span>
                </>
              )}
              <span style={{ color: CL.goldL }}>·</span>
              <span style={{ display: 'inline-flex', border: '1px solid ' + CL.goldL, borderRadius: 5, overflow: 'hidden', background: CL.cream, height: 21 }}>
                {['en', 'bg'].map(l => (
                  <button key={l} onClick={() => setCommLang(l)}
                    style={{
                      background: commLang === l ? CL.bark : 'transparent',
                      color: commLang === l ? CL.gold : CL.stone,
                      border: 'none', padding: '0 8px',
                      fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6,
                      cursor: 'pointer', fontFamily: 'inherit', textTransform: 'uppercase',
                    }}>
                    {l.toUpperCase()}
                  </button>
                ))}
              </span>
            </div>

            {naLocked ? (
              <div style={{ fontSize: 12, color: CL.stone, padding: '10px 12px', background: CL.sand, borderRadius: 8, marginBottom: 8 }}>
                {LOCK_HINT[naKey]}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  onClick={() => sendVia(chipChannel, naKey)}
                  disabled={!chipChannel}
                  style={{
                    flex: 1,
                    background: chipChannel ? CL.olive : CL.stone,
                    color: '#fff', border: 'none',
                    padding: '11px 12px', borderRadius: 10,
                    fontSize: 13, fontWeight: 600, letterSpacing: 0.3,
                    cursor: chipChannel ? 'pointer' : 'not-allowed',
                    fontFamily: 'inherit',
                  }}>
                  {lang === 'bg' ? `Изпрати чрез ${channelLabel}` : `Send via ${channelLabel}`}
                </button>
                {!naSkipped && (
                  <button
                    onClick={() => markSkipped(naKey)}
                    style={{
                      background: 'none', border: 'none', color: CL.stone,
                      fontSize: 12, fontWeight: 600, letterSpacing: 0.3,
                      textDecoration: 'underline', cursor: 'pointer',
                      padding: 0, fontFamily: 'inherit',
                    }}>
                    {lang === 'bg' ? 'Маркирай като готово' : 'Mark done'}
                  </button>
                )}
                {naSkipped && (
                  <button
                    onClick={() => unskip(naKey)}
                    style={{
                      background: 'none', border: 'none', color: CL.olive,
                      fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      padding: 0, fontFamily: 'inherit',
                    }}>
                    {lang === 'bg' ? '↩ Възстанови' : '↩ Restore'}
                  </button>
                )}
              </div>
            )}

            {circles.length > 1 && (
              <button onClick={() => setShowAll(v => !v)}
                style={{
                  display: 'block', margin: '12px auto 0',
                  background: 'none', border: 'none',
                  color: CL.gold, fontSize: 10.5, fontWeight: 600,
                  letterSpacing: 1, textTransform: 'uppercase',
                  cursor: 'pointer', padding: 0, fontFamily: 'inherit',
                }}>
                {showAll
                  ? (lang === 'bg' ? 'Скрий ▴' : 'Hide ▴')
                  : (lang === 'bg' ? `Покажете всички ${circles.length} ›` : `Show all ${circles.length} ›`)}
              </button>
            )}
          </div>
        );
      })()}

      {showPrefTip && (
        <div style={{ margin: '10px 14px 0', padding: '8px 12px', background: '#FBF6EC', border: '1px solid #E8D5B0', borderRadius: 8, fontSize: 12, color: '#8B7D6B', display: 'flex', alignItems: 'flex-start', gap: 8, lineHeight: 1.4 }}>
          <div style={{ flex: 1 }}>
            {lang === 'bg'
              ? 'Съвет: попитайте госта дали предпочита WhatsApp, Viber или имейл · ще запомним за следващия път.'
              : "Tip: ask the guest if they prefer WhatsApp, Viber, or email · we'll remember it next time."}
          </div>
          <button onClick={dismissTip} aria-label="Dismiss"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8B7D6B', fontSize: 16, padding: 0, lineHeight: 1, flexShrink: 0 }}>
            ×
          </button>
        </div>
      )}

      {(showAll || !inSurface) && (
        <>
          {/* Circle row */}
          <div style={{ display: 'flex', justifyContent: 'space-around', padding: '12px 8px', gap: 6, ...(inSurface ? { marginTop: 12, paddingTop: 12, borderTop: '1px solid ' + CL.goldL } : {}) }}>
            {circles.map(({ key, status }) => {
              const isOpen = openKey === key;
              const fill = STATUS_FILL[status];
              const dashed = status === 'skipped';
              return (
                <button key={key} onClick={() => setOpenKey(isOpen ? null : key)}
                  title={status === 'locked' ? LOCK_HINT[key] : FULL_LABELS[key]}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: 0 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 14,
                    background: fill,
                    border: dashed ? '2px dashed #fff' : 'none',
                    outline: isOpen ? '2px solid #3D2B1F' : 'none',
                    outlineOffset: 2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: 14, fontWeight: 700,
                  }}>
                    {status === 'sent' ? '✓' : status === 'skipped' ? '—' : status === 'locked' ? '🔒' : ''}
                  </div>
                  <div style={{ fontSize: 10, color: '#8B7D6B', fontWeight: 600 }}>{SHORT_LABELS[key]}</div>
                </button>
              );
            })}
          </div>

          {/* Accordion: tapped circle's message + actions */}
          {openKey && (() => {
            const item = circles.find(c => c.key === openKey);
            if (!item) return null;
            if (item.status === 'locked') {
              return (
                <div style={{ padding: '10px 14px 14px', borderTop: '1px solid #F5EFE6', fontSize: 12, color: '#8B7D6B' }}>
                  {LOCK_HINT[openKey]}
                </div>
              );
            }
            if (item.status === 'sent') {
              const sentAt = booking.messageLog?.[openKey + '_sent_at'];
              return (
                <div style={{ padding: '10px 14px 14px', borderTop: '1px solid #F5EFE6' }}>
                  <div style={{ fontSize: 13, color: CL.bark, fontWeight: 600, marginBottom: 10 }}>
                    ✓ {FULL_LABELS[openKey]} sent {formatSentAt(sentAt)}
                  </div>
                  {showMsgForSent && (
                    <div style={{ fontSize: 12, color: '#8B7D6B', background: '#F5EFE6', borderRadius: 8, padding: '8px 10px', marginBottom: 10, whiteSpace: 'pre-wrap', maxHeight: 100, overflow: 'auto', lineHeight: 1.5 }}>
                      {getMsg(openKey)}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <button
                      onClick={() => setShowMsgForSent(v => !v)}
                      style={{ background: 'none', border: 'none', color: CL.stone, fontSize: 12, cursor: 'pointer', padding: 0 }}>
                      {showMsgForSent ? '▲ Hide message' : '▼ Show message'}
                    </button>
                    <button
                      onClick={() => patchLog({ [openKey + '_sent_at']: null })}
                      style={{ background: 'none', border: 'none', color: CL.olive, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                      ↩ Unmark as sent
                    </button>
                  </div>
                </div>
              );
            }
            const msg = getMsg(openKey);
            return (
              <div style={{ padding: '10px 14px 14px', borderTop: '1px solid #F5EFE6' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#3D2B1F', marginBottom: 8 }}>{FULL_LABELS[openKey]}</div>

                {openKey === 'deposit_request' && (
                  <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: 12, color: '#8B7D6B', flexShrink: 0 }}>
                      {lang === 'bg' ? 'Сума на депозит' : 'Deposit amount'} ({(property?.currency || 'EUR').toUpperCase()})
                    </div>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="1"
                      step="0.01"
                      value={depositInput}
                      onChange={e => setDepositInput(e.target.value)}
                      disabled={generating}
                      style={{ flex: 1, padding: '6px 10px', border: '1px solid #E8D5B0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', opacity: generating ? 0.7 : 1 }}
                    />
                  </div>
                )}

                <div style={{ fontSize: 12, color: '#8B7D6B', background: '#F5EFE6', borderRadius: 8, padding: '8px 10px', marginBottom: 10, whiteSpace: 'pre-wrap', maxHeight: 100, overflow: 'auto', lineHeight: 1.5 }}>{msg}</div>

                {genError && (
                  <div style={{ fontSize: 12, color: '#C0562B', marginBottom: 8, fontWeight: 600 }}>{genError}</div>
                )}

                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  {channelOrder.map(ch => {
                    const canSend = (ch === 'email' && booking.email) || (ch !== 'email' && phone);
                    if (!canSend) return null;
                    const s = CHANNEL_STYLES[ch];
                    const isPreferred = ch === preferredChannel;
                    const disabled = generating;
                    return (
                      <button key={ch} onClick={() => sendVia(ch, openKey)} disabled={disabled} style={{ ...channelButtonStyle(ch), opacity: disabled ? 0.6 : 1, cursor: disabled ? 'wait' : 'pointer' }}>
                        <s.Icon sz={14} />
                        <span>{generating && openKey === 'deposit_request' ? (lang === 'bg' ? 'Генериране...' : 'Generating...') : s.label}</span>
                        {isPreferred && !generating && <span style={{ fontSize: 10 }}>★</span>}
                      </button>
                    );
                  })}
                </div>
                {item.status === 'skipped' ? (
                  <button onClick={() => unskip(openKey)}
                    style={{ background: 'none', border: 'none', color: '#5A7247', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                    {lang === 'bg' ? '↩ Възстановете и изпратете' : '↩ Unskip and send'}
                  </button>
                ) : item.status !== 'sent' ? (
                  <button onClick={() => markSkipped(openKey)}
                    style={{ background: 'none', border: 'none', color: '#8B7D6B', fontSize: 12, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                    {lang === 'bg' ? 'Пропуснете за тази резервация, не е нужно' : 'Skip this for this booking — not needed'}
                  </button>
                ) : null}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

export { TodayActionsFeed, CommPanel, PaymentQuickPick };
